import {Injectable} from '@angular/core';
import {ThemeService} from '@app/services/theme.service';

declare const CKEDITOR: any;

/**
 * Colours for every CKEditor *editing* iframe, added once via `CKEDITOR.addCss` — the app's
 * own stylesheet (src/theme/_ckeditor.scss) cannot reach inside the iframe. `sb-dark` is
 * toggled on the editing body to track the app's theme. The font is not set here: it
 * follows the panel text around each editor, so it is applied per editor (applyFont).
 */
const EDITOR_AREA_CSS = `
  body {
    background: transparent !important;
    color: #3b3e66;
  }

  a {
    color: #4bb7e3;
  }

  body.sb-dark {
    color: #e5e7eb;
  }

  body.sb-dark a {
    color: #60a5fa;
  }
`;

/** Same idea, for the small iframe the FontSize / TextColor panels render their grid in. */
const PANEL_CSS = `
  body {
    background: #ffffff !important;
    color: #3b3e66 !important;
  }

  body.sb-dark {
    background: #1f2937 !important;
    color: #e5e7eb !important;
  }

  a.cke_colorbox:hover,
  a.cke_colorbox_selected,
  .cke_panel_list_item a:hover {
    background: rgba(75, 183, 227, 0.14) !important;
  }
`;

const PANEL_STYLE_ID = 'sb-ckeditor-panel-theme';
const FONT_STYLE_ID = 'sb-ckeditor-font';

/** Where auto-grow stops and the editing area starts to scroll, unless the config says. */
const AUTO_GROW_MAX_HEIGHT = 480;

/**
 * Starts the app-wide CKEditor behaviour: dark mode, read-only styling, the panel's font,
 * panel theming and auto-grow for every CKEditor instance in the app, with no per-usage
 * template changes. Started once from `app.component.ts`, next to `themeService.loadTheme()`.
 *
 * CSS that the page's own stylesheet can reach (chrome, toolbar, dialogs) lives in
 * `src/theme/_ckeditor.scss` instead — this service only handles what that CSS cannot reach:
 * inside the editing iframe, and inside dialog/panel iframes.
 */
@Injectable({providedIn: 'root'})
export class CkeditorThemeService {
  private started = false;

  private readonly fontFaceCache = new Map<string, string>();

  constructor(private themeService: ThemeService) {
  }

  start(): void {
    if (this.started) {
      return;
    }
    this.started = true;

    this.whenCkeditorReady(() => {
      CKEDITOR.addCss(EDITOR_AREA_CSS);
      CKEDITOR.on('instanceReady', (evt: any) => this.onInstanceReady(evt.editor));

      this.themeService.isDarkTheme$.subscribe((isDark) => this.applyToAllInstances(isDark));
    });
  }

  private whenCkeditorReady(fn: () => void): void {
    if (typeof CKEDITOR !== 'undefined') {
      fn();
      return;
    }
    // The global CKEditor script can still be loading when the app boots; retry briefly
    // instead of silently doing nothing for the rest of the session.
    let attempts = 0;
    const timer = setInterval(() => {
      attempts++;
      if (typeof CKEDITOR !== 'undefined') {
        clearInterval(timer);
        fn();
      } else if (attempts > 50) {
        clearInterval(timer);
      }
    }, 100);
  }

  private onInstanceReady(editor: any): void {
    if (!editor) {
      return;
    }

    const themeContent = () => {
      this.applyDarkClass(editor, this.isDarkNow());
      this.applyFont(editor.document?.$, this.hostFontFamily(editor));
    };
    themeContent();
    this.applyReadOnlyClass(editor, !!editor.readOnly);

    editor.on('contentDom', themeContent);
    editor.on('readOnly', () => this.applyReadOnlyClass(editor, !!editor.readOnly));
    editor.on('panelShow', (evt: any) => this.themePanel(evt?.data, this.isDarkNow()));

    this.enableAutoGrow(editor);
  }

  private applyToAllInstances(isDark: boolean): void {
    if (typeof CKEDITOR === 'undefined') {
      return;
    }
    Object.keys(CKEDITOR.instances || {}).forEach((name) => {
      this.applyDarkClass(CKEDITOR.instances[name], isDark);
    });
  }

  private isDarkNow(): boolean {
    return typeof document !== 'undefined' && document.body.classList.contains('dark-theme');
  }

  private applyDarkClass(editor: any, isDark: boolean): void {
    try {
      const body = editor?.document?.getBody?.();
      if (!body) {
        return;
      }
      if (isDark) {
        body.addClass('sb-dark');
      } else {
        body.removeClass('sb-dark');
      }
    } catch {
      // Editor may be mid-teardown; nothing to theme.
    }
  }

  private applyReadOnlyClass(editor: any, isReadOnly: boolean): void {
    try {
      const container = editor?.container;
      if (!container?.addClass) {
        return;
      }
      if (isReadOnly) {
        container.addClass('cke_read_only_container');
      } else {
        container.removeClass('cke_read_only_container');
      }
    } catch {
      // Ignore — same as above.
    }
  }

  /**
   * `panel` is the CKEDITOR floatPanel that just showed (FontSize, TextColor, …). Its grid
   * renders inside its own iframe, which CKEditor doesn't expose through a stable public
   * getter, so this walks the known shape defensively and gives up quietly if it doesn't
   * match — a missed panel theme is cosmetic, not worth breaking the editor over.
   */
  private themePanel(panel: any, isDark: boolean): void {
    try {
      const iframeEl: HTMLIFrameElement | undefined =
        panel?._?.iframe?.$ ||
        panel?.element?.$?.getElementsByTagName?.('iframe')?.[0];
      const doc: Document | undefined = iframeEl?.contentDocument || undefined;
      if (!doc?.body) {
        return;
      }

      if (!doc.getElementById(PANEL_STYLE_ID)) {
        const style = doc.createElement('style');
        style.id = PANEL_STYLE_ID;
        style.textContent = PANEL_CSS;
        (doc.head || doc.body).appendChild(style);
      }

      // Panels hang off <body>, so they take the body's font, like the dialogs do.
      this.applyFont(doc, getComputedStyle(document.body).fontFamily);
      doc.body.classList.toggle('sb-dark', isDark);
    } catch {
      // See above.
    }
  }

  // --- Font -----------------------------------------------------------------------------
  // The editing area uses the font of the panel text around the editor, the same font
  // _ckeditor.scss hands the toolbar through `font-family: inherit`. An iframe does not see
  // the page's @font-face rules, so the ones for that font are copied in.

  private hostFontFamily(editor: any): string {
    const host: Element = editor?.container?.$?.parentElement || document.body;
    return getComputedStyle(host).fontFamily || getComputedStyle(document.body).fontFamily;
  }

  private applyFont(doc: Document | undefined, fontFamily: string): void {
    try {
      if (!doc?.documentElement || !fontFamily) {
        return;
      }
      let style = doc.getElementById(FONT_STYLE_ID);
      if (!style) {
        style = doc.createElement('style');
        style.id = FONT_STYLE_ID;
        (doc.head || doc.documentElement).appendChild(style);
      }
      // `html body` outranks the `body` rules in contents.css and in a config's contentsCss.
      const css = `${this.fontFaceCss(fontFamily)}\nhtml body { font-family: ${fontFamily}; }`;
      if (style.textContent !== css) {
        style.textContent = css;
      }
    } catch {
      // The document may be mid-reload; contentDom applies it again.
    }
  }

  /**
   * The page's @font-face rules for the families in `fontFamily`, with their URLs made
   * absolute — they are written relative to the stylesheet they came from, and an injected
   * <style> would resolve them against the iframe's document instead.
   */
  private fontFaceCss(fontFamily: string): string {
    const cached = this.fontFaceCache.get(fontFamily);
    if (cached !== undefined) {
      return cached;
    }

    const wanted = new Set(fontFamily.split(',').map((name) => this.normalizeFamily(name)).filter(Boolean));
    const rules: string[] = [];
    Array.from(document.styleSheets).forEach((sheet) => this.collectFontFaces(sheet, wanted, rules));

    const css = rules.join('\n');
    this.fontFaceCache.set(fontFamily, css);
    return css;
  }

  private collectFontFaces(sheet: CSSStyleSheet, wanted: Set<string>, rules: string[]): void {
    let list: CSSRuleList;
    try {
      list = sheet.cssRules;
    } catch {
      return; // A cross-origin stylesheet; its rules cannot be read.
    }
    const base = sheet.href || document.baseURI;

    const walk = (ruleList: CSSRuleList) => {
      Array.from(ruleList).forEach((rule) => {
        if (rule instanceof CSSFontFaceRule) {
          if (wanted.has(this.normalizeFamily(rule.style.getPropertyValue('font-family')))) {
            rules.push(rule.cssText.replace(/url\(\s*(['"]?)([^'")]+)\1\s*\)/g,
              (_match, _quote, url) => `url("${new URL(url, base).href}")`));
          }
        } else if (rule instanceof CSSImportRule) {
          if (rule.styleSheet) {
            this.collectFontFaces(rule.styleSheet, wanted, rules);
          }
        } else if ((rule as CSSGroupingRule).cssRules) {
          walk((rule as CSSGroupingRule).cssRules);
        }
      });
    };
    walk(list);
  }

  private normalizeFamily(name: string): string {
    return (name || '').trim().replace(/^['"]|['"]$/g, '').toLowerCase();
  }

  // --- Auto-grow ------------------------------------------------------------------------
  // On by default for every instance: the editing area grows and shrinks with its content,
  // between the configured height (or whatever height the user last dragged the resize grip
  // to) and `snoballAutoGrowMaxHeight`, after which it scrolls. `snoballAutoGrow: false` in
  // an editor's config opts out — sb-textarea does, because it sizes itself.

  private enableAutoGrow(editor: any): void {
    if (editor?.config?.snoballAutoGrow === false) {
      return;
    }

    let frame = 0;
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => this.autoGrow(editor));
    };

    editor.on('contentDom', () => this.observeContentHeight(editor, schedule));
    // Only the resize grip (and maximize) fire this; auto-grow sets the height directly, so
    // anything seen here is the user's own choice and becomes the new minimum.
    editor.on('resize', (evt: any) => {
      if (this.isMaximized(editor)) {
        return;
      }
      const height = Number(evt?.data?.contentsHeight);
      if (height > 0) {
        editor.snoballAutoGrowMinHeight = height;
      }
    });
    editor.on('destroy', () => {
      cancelAnimationFrame(frame);
      editor.snoballContentObserver?.disconnect?.();
    });

    this.observeContentHeight(editor, schedule);
  }

  // A ResizeObserver on the editing document's root catches every content change —
  // typing, setData, paste, images loading — and the editor first becoming visible inside
  // a collapsed section, which no editor event reports.
  private observeContentHeight(editor: any, schedule: () => void): void {
    try {
      const doc: Document | undefined = editor.document?.$;
      const win: any = doc?.defaultView;
      if (!doc?.documentElement || !win || editor.snoballContentObserverDoc === doc) {
        return;
      }
      editor.snoballContentObserver?.disconnect?.();
      const Observer = win.ResizeObserver || (window as any).ResizeObserver;
      if (Observer) {
        const observer = new Observer(schedule);
        observer.observe(doc.documentElement);
        editor.snoballContentObserver = observer;
      }
      editor.snoballContentObserverDoc = doc;
      schedule();
    } catch {
      // Mid-teardown or mid-reload; the next contentDom tries again.
    }
  }

  private autoGrow(editor: any): void {
    try {
      if (editor.status === 'destroyed' || this.isMaximized(editor)) {
        return;
      }
      // Hidden (e.g. a collapsed section): nothing to measure yet. The observer fires again
      // once the editor is laid out.
      if (!editor.container?.$?.offsetParent) {
        return;
      }
      const doc: Document | undefined = editor.document?.$;
      const contents = editor.ui?.space?.('contents');
      if (!doc?.documentElement || !contents) {
        return;
      }

      // The root's own box is sized by the content, not by the iframe it sits in.
      const contentHeight = Math.ceil(doc.documentElement.getBoundingClientRect().height);
      if (!contentHeight) {
        return;
      }
      const configured = parseInt(editor.config?.height, 10) || 0;
      const min = Math.max(configured, editor.snoballAutoGrowMinHeight || 0);
      const max = Math.max(min, Number(editor.config?.snoballAutoGrowMaxHeight) || AUTO_GROW_MAX_HEIGHT);
      const target = Math.min(Math.max(min, contentHeight), max);

      // No scrollbar below the cap: one appearing mid-growth narrows the text, re-wraps it
      // and makes the height bounce.
      doc.documentElement.style.overflowY = contentHeight > max ? 'auto' : 'hidden';
      if (contents.getStyle('height') !== `${target}px`) {
        contents.setStyle('height', `${target}px`);
      }
    } catch {
      // Mid-teardown; nothing to size.
    }
  }

  private isMaximized(editor: any): boolean {
    return typeof CKEDITOR !== 'undefined' &&
      editor?.getCommand?.('maximize')?.state === CKEDITOR.TRISTATE_ON;
  }
}
